import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import { v4 as uuid } from "uuid";
import axiosClient from "../api/AxiosClient";
import { Agents } from "../types/master";
import { RootState } from ".";
import { safeJSONParse, safeParse } from "../utils/helper";
import { fetchAgents } from "./agentSlice";
import { getAgentStartedSteps } from "../utils/constants";
import {
  normalizeTextEncoding,
  deepNormalizeStrings,
} from "../utils/textFormatter";

export interface ChatMessage {
  sessionId: string | null;
  id: string;
  sender: "user" | "agent";
  message: string;
  questionId?: string;
  timestamp: string;
  requestId?: string; // store agent requestId
  agentIdName?: string;
  workflowId?: string;
  files?: Array<{ name: string; size: number }>;
  feedback?: "liked" | "disliked" | null;
  isImageGeneration?: boolean;
  chatId?: number; // chat_id from API response for user preferences
  userPreference?: 0 | 1 | null; // 0 for disliked, 1 for liked, null for no preference
}

export interface StreamProgress {
  executorId: string;
  timestamp: number;
}

export interface RfqQuestion {
  question_id: string;
  question_text: string;
  question_type: string;
  options: string[];
  is_required: boolean;
  help_text: string | null;
}

export interface RfqAnswer {
  questionId: string;
  questionText: string;
  answer: string;
  timestamp: string;
}

interface RfqStep {
  stepNumber: number;
  stepName: string;
  response: any; // Full response data for that step
  timestamp: string;
}

interface UserChatState {
  selectedAgent: Agents;
  chatHistory: { [agentId: number]: ChatMessage[] };
  currentChat: ChatMessage[];
  loading: boolean;
  workflowId: string;
  requestId: string;
  streamProgress: StreamProgress[];
  isStreaming: boolean;
  isViewingHistory: boolean;
  rfqQuestions: { [sessionId: string]: { [questionId: string]: RfqQuestion } };
  rfqAnswers: { [sessionId: string]: RfqAnswer[] };
  rfqStepHistory: { [sessionId: string]: RfqStep[] };
  rfqCurrentStep: { [sessionId: string]: number };
}

const initialState: UserChatState = {
  selectedAgent: {} as Agents, // Will be set automatically when agents are fetched
  chatHistory: {},
  currentChat: [],
  loading: false,
  workflowId: "",
  requestId: "",
  streamProgress: [],
  isStreaming: false,
  isViewingHistory: false,
  rfqQuestions: {},
  rfqAnswers: {},
  rfqStepHistory: {},
  rfqCurrentStep: {},
};

// ---------------- THUNK ----------------
export const sendMessageToAgent = createAsyncThunk(
  "user/sendMessageToAgent",
  async (
    {
      agentId,
      message,
      questionId,
      timestamp,
      agentEndpoint,
      initialStreamExecutorId,
      sessionId,
      files,
      our_file,
      counterparty_file,
      interview_jd,
      interview_resume,
      interview_prescreening,
      isImageGeneration = false,
      translation,
      translationType,
      procurement_type,
      accept_array,
      reject_array,
      modify_array,
      consolidator_accept_array,
      consolidator_reject_array,
      consolidator_modify_array,
      contract_reviewer_accept_array,
      contract_reviewer_reject_array,
      contract_reviewer_modify_array,
      contract_reviewer_new_array,
      add_new_array,
      thread_id,
    }: {
      agentId: number;
      message: string;
      questionId: string;
      timestamp: string;
      agentEndpoint: string | undefined;
      initialStreamExecutorId?: string;
      sessionId: string | null;
      files?: File[];
      our_file?: File;
      counterparty_file?: File;
      interview_jd?: File;
      interview_resume?: File;
      interview_prescreening?: File;
      isImageGeneration?: boolean;
      translation?: "english" | "arabic";
      translationType?: "general" | "legal";
      procurement_type?: "sow_filler" | "reviewer" | "redraft";
      accept_array?: any[];
      reject_array?: any[];
      modify_array?: any[];
      consolidator_accept_array?: any[];
      consolidator_reject_array?: any[];
      consolidator_modify_array?: any[];
      contract_reviewer_accept_array?: string;
      contract_reviewer_reject_array?: string;
      contract_reviewer_modify_array?: string;
      contract_reviewer_new_array?: string;
      add_new_array?: any[];
      thread_id?: string;
    },
    { rejectWithValue, getState, dispatch },
  ) => {
    const state = getState() as RootState;
    const currentUser = state.auth.user;
    const userEmail = currentUser?.username;
    console.log("Agent Name", agentId);

    // Use retained IDs from state
    const prevRequestId = state.user.requestId;
    const workflow_id = state.user.workflowId;

    // Get chatId from the last agent response in current chat
    const currentChat = state.user.currentChat;
    const lastAgentMessage = [...currentChat]
      .reverse()
      .find((msg) => msg.sender === "agent");
    const previousChatId = lastAgentMessage?.chatId || null;

    // Get filters from state for agent ID 18
    const hrAgentFilters = state.filters.hrAgentFilters;

    // Build filters object for agent ID 18
    const buildFiltersForAgentId18 = () => {
      if (agentId !== 18) return null;

      const filters: Record<string, any> = {};

      if (hrAgentFilters.experience) {
        filters.experience = hrAgentFilters.experience.value;
      }

      if (hrAgentFilters.location && hrAgentFilters.location.length > 0) {
        filters.location = hrAgentFilters.location.map((loc) => loc.value);
      }

      if (hrAgentFilters.degree && hrAgentFilters.degree.length > 0) {
        filters.degree = hrAgentFilters.degree.map((deg) => deg.value);
      }

      if (
        hrAgentFilters.previousCompany &&
        hrAgentFilters.previousCompany.length > 0
      ) {
        filters.previousCompany = hrAgentFilters.previousCompany.map(
          (comp) => comp.value,
        );
      }

      // Only return filters if at least one filter has a value
      return Object.keys(filters).length > 0 ? filters : null;
    };

    const filters = buildFiltersForAgentId18();

    // Detect if intake agent files are present
    const hasIntakeAgentFiles =
      interview_jd || interview_resume || interview_prescreening;

    // Handle file metadata for agent 52 and intake agent
    let fileMetadata: Array<{ name: string; size: number }> | undefined;
    if (agentId === 52) {
      fileMetadata = [];
      if (our_file) {
        fileMetadata.push({
          name: `Our document: ${our_file.name}`,
          size: our_file.size,
        });
      }
      if (counterparty_file) {
        fileMetadata.push({
          name: `Counterparty document: ${counterparty_file.name}`,
          size: counterparty_file.size,
        });
      }
    } else if (hasIntakeAgentFiles) {
      fileMetadata = [];
      if (interview_jd) {
        fileMetadata.push({
          name: `Job Description: ${interview_jd.name}`,
          size: interview_jd.size,
        });
      }
      if (interview_resume) {
        fileMetadata.push({
          name: `Resume: ${interview_resume.name}`,
          size: interview_resume.size,
        });
      }
      if (interview_prescreening) {
        fileMetadata.push({
          name: `Pre-screening: ${interview_prescreening.name}`,
          size: interview_prescreening.size,
        });
      }
    } else {
      fileMetadata = files?.map((file) => ({
        name: file.name,
        size: file.size,
      }));
    }

    const userMsg: ChatMessage = {
      sessionId,
      id: questionId,
      sender: "user",
      message,
      timestamp,
      files: fileMetadata,
      isImageGeneration,
    };

    try {
      // Special handling for Job Intake agent (ID: 110) with file upload
      if (agentId === 110 && files && files.length > 0) {
        console.log(
          "🔄 Job Intake agent detected with file - calling /stream/job-intake/upload",
        );

        const formData = new FormData();
        formData.append("file", files[0]); // Job Intake expects single file
        if (currentUser?.username) {
          formData.append("hm_email", currentUser.username);
        }
        const hmName = currentUser?.idTokenClaims?.name as string | undefined;
        if (hmName) {
          formData.append("hm_name", hmName);
        }

        try {
          const uploadResponse = await fetch(
            `${import.meta.env.VITE_API_BASE_URL}/stream/job-intake/upload`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${localStorage.getItem("access_token")}`,
              },
              body: formData,
            },
          );

          if (!uploadResponse.ok) {
            throw new Error(`Upload failed: ${uploadResponse.statusText}`);
          }

          const uploadData = await uploadResponse.json();
          console.log("✅ Job Intake upload response:", uploadData);

          // Create agent message with the upload response
          const agentMsg: ChatMessage = {
            sessionId: uploadData.session_id || sessionId,
            id: uuid(),
            sender: "agent",
            message: JSON.stringify(uploadData),
            questionId,
            timestamp: new Date().toISOString(),
            requestId: prevRequestId,
            agentIdName: "job_intake_agent",
            workflowId: workflow_id,
          };

          return { agentId, userMsg, agentMsg };
        } catch (uploadError: any) {
          console.error("❌ Job Intake upload error:", uploadError);
          const agentMsg: ChatMessage = {
            sessionId,
            id: uuid(),
            sender: "agent",
            message: `Upload failed: ${uploadError.message}`,
            questionId,
            timestamp: new Date().toISOString(),
            requestId: prevRequestId,
          };
          return { agentId, userMsg, agentMsg };
        }
      }

      // Check if this is a streaming endpoint
      const isStreamingEndpoint = agentEndpoint?.startsWith("/stream");

      // Prepare request data once (used for both streaming and non-streaming)
      let requestBody: FormData | string | Record<string, any>;
      const hasFiles = files && files.length > 0;
      const hasAgent52Files = our_file || counterparty_file;
      // hasIntakeAgentFiles already defined above (line ~211) for use in file metadata

      // Debug logging for file upload
      if (agentId === 52) {
        console.log("🔍 Agent 52 file upload debug:", {
          hasFiles,
          filesLength: files?.length,
          filesArray: files,
          hasAgent52Files,
          our_file,
          counterparty_file,
        });
      }

      // Check if this is the MOU endpoint (agent ID 54)
      const isMouEndpoint = agentEndpoint === "/stream/mou" || agentId === 54;

      if (hasFiles || hasAgent52Files || hasIntakeAgentFiles) {
        const formData = new FormData();

        // MOU endpoint uses "message" field, others use "input"
        if (isMouEndpoint) {
          formData.append("message", message);
          formData.append("workflow_id", workflow_id);
          formData.append("request_id", prevRequestId);
        } else {
          formData.append("input", message);
          formData.append("user_email_id", userEmail || "");
          formData.append("agent_id", agentId.toString());
          formData.append("request_id", prevRequestId);
          formData.append(
            "previous_chat_id",
            previousChatId !== null ? previousChatId.toString() : "",
          );
          formData.append("is_approved", "0");
          formData.append("approved_by", "0");
          formData.append("score", "0");
          formData.append("workflow_id", workflow_id);
          formData.append("is_image_generation", isImageGeneration.toString());
        }

        if (isStreamingEndpoint && !isMouEndpoint) {
          formData.append("response_type", "stream");
        }

        // Add filters for agent ID 18
        if (filters) {
          formData.append("filters", JSON.stringify(filters));
        }

        // Add translation settings for agent ID 7
        if (translation) {
          formData.append("translation", translation);
        }
        if (translationType) {
          formData.append("translation_type", translationType);
        }

        // Add procurement type for agent ID 20
        if (procurement_type) {
          formData.append("procurement_type", procurement_type);
        }

        // Add template reviewer feedback arrays (empty string if no items)
        formData.append(
          "accept_array",
          accept_array && accept_array.length > 0
            ? JSON.stringify(accept_array)
            : "",
        );
        formData.append(
          "reject_array",
          reject_array && reject_array.length > 0
            ? JSON.stringify(reject_array)
            : "",
        );
        formData.append(
          "modify_array",
          modify_array && modify_array.length > 0
            ? JSON.stringify(modify_array)
            : "",
        );

        // Add consolidator feedback arrays if present
        if (consolidator_accept_array && consolidator_accept_array.length > 0) {
          formData.append(
            "consolidator_accept_array",
            JSON.stringify(consolidator_accept_array),
          );
        }
        if (consolidator_reject_array && consolidator_reject_array.length > 0) {
          formData.append(
            "consolidator_reject_array",
            JSON.stringify(consolidator_reject_array),
          );
        }
        if (consolidator_modify_array && consolidator_modify_array.length > 0) {
          formData.append(
            "consolidator_modify_array",
            JSON.stringify(consolidator_modify_array),
          );
        }

        // Add contract reviewer feedback arrays if present
        if (contract_reviewer_accept_array) {
          formData.append(
            "contract_reviewer_accept_array",
            contract_reviewer_accept_array,
          );
        }
        if (contract_reviewer_reject_array) {
          formData.append(
            "contract_reviewer_reject_array",
            contract_reviewer_reject_array,
          );
        }
        if (contract_reviewer_modify_array) {
          formData.append(
            "contract_reviewer_modify_array",
            contract_reviewer_modify_array,
          );
        }
        if (contract_reviewer_new_array) {
          formData.append(
            "contract_reviewer_new_array",
            contract_reviewer_new_array,
          );
        }

        // Add new array for template reviewer checklist if present
        if (add_new_array && add_new_array.length > 0) {
          formData.append("add_new_array", JSON.stringify(add_new_array));
        }

        // Handle files for agent 52 (our_file and counterparty_file)
        if (agentId === 52) {
          if (our_file) {
            console.log("✅ Appending our_file to FormData:", our_file.name);
            formData.append("our_file", our_file);
          }
          if (counterparty_file) {
            console.log("✅ Appending counterparty_file to FormData:", counterparty_file.name);
            formData.append("counterparty_file", counterparty_file);
          }
          // Append thread_id if provided
          if (thread_id) {
            console.log("✅ Appending thread_id to FormData:", thread_id);
            formData.append("thread_id", thread_id);
          }
          // Also handle standard files array for agent 52 if provided
          if (files && files.length > 0) {
            console.log("✅ Agent 52 also has standard files array, appending:", files.length, "files");
            files.forEach((file, index) => {
              console.log(`  - File ${index + 1}:`, file.name);
              formData.append("files", file);
            });
          }
        } else if (hasIntakeAgentFiles) {
          // Handle files for intake agent (interview_jd, interview_resume, interview_prescreening)
          if (interview_jd) {
            formData.append("interview_jd", interview_jd);
          }
          if (interview_resume) {
            formData.append("interview_resume", interview_resume);
          }
          if (interview_prescreening) {
            formData.append("interview_prescreening", interview_prescreening);
          }
        } else {
          // Handle files for other agents
          files?.forEach((file, index) => {
            console.log(`✅ Appending file ${index + 1} to FormData:`, file.name);
            formData.append("files", file);
          });
        }

        requestBody = formData;
      } else {
        const payload = {
          input: message,
          user_email_id: userEmail,
          agent_id: agentId,
          request_id: prevRequestId,
          previous_chat_id: previousChatId !== null ? previousChatId : "",
          is_approved: 0,
          approved_by: 0,
          score: 0,
          workflow_id: workflow_id,
          is_image_generation: isImageGeneration,
          ...(isStreamingEndpoint && { response_type: "stream" }),
          ...(filters && { filters }),
          ...(translation && { translation }),
          ...(translationType && { translation_type: translationType }),
          ...(procurement_type && { procurement_type }),
          accept_array:
            accept_array && accept_array.length > 0
              ? JSON.stringify(accept_array)
              : "",
          reject_array:
            reject_array && reject_array.length > 0
              ? JSON.stringify(reject_array)
              : "",
          modify_array:
            modify_array && modify_array.length > 0
              ? JSON.stringify(modify_array)
              : "",
          ...(consolidator_accept_array?.length && {
            consolidator_accept_array: JSON.stringify(
              consolidator_accept_array,
            ),
          }),
          ...(consolidator_reject_array?.length && {
            consolidator_reject_array: JSON.stringify(
              consolidator_reject_array,
            ),
          }),
          ...(consolidator_modify_array?.length && {
            consolidator_modify_array: JSON.stringify(
              consolidator_modify_array,
            ),
          }),
          ...(contract_reviewer_accept_array && {
            contract_reviewer_accept_array: contract_reviewer_accept_array,
          }),
          ...(contract_reviewer_reject_array && {
            contract_reviewer_reject_array: contract_reviewer_reject_array,
          }),
          ...(contract_reviewer_modify_array && {
            contract_reviewer_modify_array: contract_reviewer_modify_array,
          }),
          ...(contract_reviewer_new_array && {
            contract_reviewer_new_array: contract_reviewer_new_array,
          }),
          ...(add_new_array?.length && {
            add_new_array: JSON.stringify(add_new_array),
          }),
          ...(thread_id && { thread_id }),
        };

        requestBody = isStreamingEndpoint ? JSON.stringify(payload) : payload;
      }

      if (isStreamingEndpoint) {
        // STREAMING LOGIC - For /stream/campaign, /stream/hr-workflow, /stream/hr-inference, /stream/jd-composer, /stream/hr-screening endpoints
        const isHrWorkflowEndpoint =
          agentEndpoint === "/stream/hr-workflow" ||
          agentEndpoint === "/stream/hr-inference" ||
          agentEndpoint === "/stream/jd-composer" ||
          agentEndpoint === "/stream/hr-screening" ||
          agentEndpoint === "/stream/agent_cv_screener";

        let token: string | null = localStorage.getItem("access_token");

        let headers: Record<string, string> = {
          Authorization: `Bearer ${token}`,
          Accept: "text/event-stream", // Tell server we expect SSE
          "Cache-Control": "no-cache", // Prevent caching of stream
          Connection: "keep-alive", // Keep connection open for streaming
        };

        // Only set Content-Type to JSON if there are NO files at all (including agent 52 files and intake agent files)
        if (!hasFiles && !hasAgent52Files && !hasIntakeAgentFiles) {
          headers["Content-Type"] = "application/json";
        }

        console.log("Making streaming request with headers:", headers);

        // Make streaming request with proper configuration
        const response = await fetch(
          `${import.meta.env.VITE_API_BASE_URL}${agentEndpoint}`,
          {
            method: "POST",
            headers,
            body: requestBody as FormData | string,
          },
        );

        console.log(
          "Stream response received:",
          response.status,
          response.headers.get("content-type"),
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) {
          throw new Error("No reader available");
        }

        console.log("Stream reader created, starting to read chunks...");

        const decoder = new TextDecoder();
        let buffer = "";
        let lastCompletedPayload: any = null;
        let lastEventStatus: string = "";
        let chunkCount = 0;

        // For HR workflow endpoint
        let hrWorkflowResult: any = null;
        let hrWorkflowId: string = "";
        let hasHrWorkflowId = false;

        // Stream progress is reset in the pending reducer.
        // For NDA we also seed the first executor step there so the UI never
        // falls back to the generic "Thinking" placeholder.

        while (true) {
          const { done, value } = await reader.read();

          if (done) {
            console.log(
              `Stream completed. Total chunks received: ${chunkCount}`,
            );
            break;
          }

          chunkCount++;
          console.log(
            `📦 Chunk ${chunkCount} received, size: ${value?.length} bytes`,
          );

          // Decode the chunk and normalise any Windows-1252 characters that
          // arrived as C1 Unicode control-code-points (U+0080–U+009F).
          const chunk = normalizeTextEncoding(
            decoder.decode(value, { stream: true }),
          );
          buffer += chunk;

          // Split buffer by newlines to process complete lines
          const lines = buffer.split("\n");

          // Keep the last incomplete line in the buffer
          buffer = lines.pop() || "";

          // Process each complete line
          for (const line of lines) {
            const trimmed = line.trim();
            if (!trimmed) continue;

            try {
              let event: any;

              // Handle SSE format (data: {...})
              if (trimmed.startsWith("data: ")) {
                const jsonStr = trimmed.substring(6);
                event = JSON.parse(jsonStr);
              }
              // Handle plain JSON
              else if (trimmed.startsWith("{")) {
                event = JSON.parse(trimmed);
              } else {
                continue;
              }

              console.log("Stream event received:", event);

              if (isHrWorkflowEndpoint) {
                // Handle HR workflow streaming events
                if (
                  (event.type === "start" || event.type === "modify") &&
                  event.agent_name
                ) {
                  console.log(
                    "HR Workflow event:",
                    event.type,
                    event.agent_name,
                  );
                  if ("workflow_id" in event) {
                    hrWorkflowId = event.workflow_id ?? "";
                    hasHrWorkflowId = true;
                  }

                  // For modify events, dispatch "modify_requirements" instead of the actual agent
                  const executorId =
                    event.type === "modify"
                      ? "modify_requirements"
                      : event.agent_name;

                  dispatch(
                    addStreamProgress({
                      executorId: executorId,
                      timestamp: Date.now(),
                    }),
                  );
                  // Small delay to ensure Redux state updates propagate
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }

                // Handle request_info events with result
                if (event.type === "request_info" && event.result) {
                  console.log("HR Workflow request_info received");
                  hrWorkflowResult = event.result;
                  if ("workflow_id" in event) {
                    hrWorkflowId = event.workflow_id ?? "";
                    hasHrWorkflowId = true;
                  }
                }

                // Handle workflow_complete events
                if (event.type === "workflow_complete") {
                  console.log("HR Workflow completed");
                  if ("workflow_id" in event) {
                    hrWorkflowId = event.workflow_id ?? "";
                    hasHrWorkflowId = true;
                  }
                }
              } else {
                // Handle campaign streaming events
                // Handle executor_invoked events for progress tracking
                if (event.type === "executor_invoked" && event.executor_id) {
                  console.log("Executor invoked:", event.executor_id);
                  dispatch(
                    addStreamProgress({
                      executorId: event.executor_id,
                      timestamp: Date.now(),
                    }),
                  );
                  // Small delay to ensure Redux state updates propagate
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }

                // Handle progress events for RFQ Evaluator and similar agents
                if (event.type === "progress" && event.agent_id) {
                  console.log(
                    "Progress event:",
                    event.agent_id,
                    "stage:",
                    event.stage,
                  );
                  dispatch(
                    addStreamProgress({
                      executorId: event.agent_id,
                      timestamp: Date.now(),
                    }),
                  );
                  // Small delay to ensure Redux state updates propagate
                  await new Promise((resolve) => setTimeout(resolve, 10));
                }

                // // Capture agent response messages (without "status" wrapper)
                // if (event.agent_id && event.result && !event.status && !event.type) {
                //   agentResponses.push(event);
                // }

                // Handle completed, awaiting_confirmation, awaiting_scoring_doc, criteria_parsed, question_answered, or awaiting_rfp_response events with payload
                if (
                  (event.status === "completed" ||
                    event.status === "awaiting_confirmation" ||
                    event.status === "awaiting_scoring_doc" ||
                    event.status === "criteria_parsed" ||
                    event.status === "question_answered" ||
                    event.status === "awaiting_rfp_response") &&
                  event.payload
                ) {
                  console.log(`${event.status} event received with payload`);
                  lastCompletedPayload = event.payload;
                  lastEventStatus = event.status;
                }
              }
            } catch (e) {
              console.warn("Failed to parse stream line:", trimmed, e);
            }
          }
        }

        let finalResponseAnswer = "";
        let newRequestId = "";
        let agentIdName = "";
        let workflowId = "";
        let chatId: number | undefined = undefined;

        if (isHrWorkflowEndpoint) {
          // Handle HR workflow response
          console.log("HR Workflow Result:", hrWorkflowResult);
          workflowId = hasHrWorkflowId ? hrWorkflowId : workflow_id;
          // Use explicit empty requestId if sent by agent, only fall back to prevRequestId if undefined
          newRequestId =
            hrWorkflowResult?.requestId !== undefined
              ? (hrWorkflowResult.requestId ?? "")
              : prevRequestId;
          chatId = hrWorkflowResult?.chat_id;
          console.log("Extracted chatId for HR workflow:", chatId);

          // Check if this was a modify request by looking at streamProgress
          const currentStateCheck = getState() as RootState;
          const wasModifyRequest = currentStateCheck.user.streamProgress.some(
            (progress) => progress.executorId === "modify_requirements",
          );

          // Use "modify_requirements" as agentIdName if it was a modify request
          agentIdName = hrWorkflowResult?.data?.agent_name;

          const answer = hrWorkflowResult?.data?.output;
          const question = hrWorkflowResult?.data?.prompt;
          const stepNumber = hrWorkflowResult?.data?.step_num;

          finalResponseAnswer = JSON.stringify(
            deepNormalizeStrings({
              answer,
              question,
              stepNumber,
            }),
          );
        } else {
          // Handle campaign workflow response
          console.log("Last Completed Payload:", lastCompletedPayload);
          workflowId =
            lastCompletedPayload?.workflow_id !== undefined
              ? (lastCompletedPayload.workflow_id ?? "")
              : workflow_id;
          // Use explicit empty requestId if sent by agent, only fall back to prevRequestId if undefined
          newRequestId =
            lastCompletedPayload?.result?.requestId !== undefined
              ? (lastCompletedPayload.result.requestId ?? "")
              : prevRequestId;
          chatId = lastCompletedPayload?.result?.chat_id;
          console.log("Extracted chatId for campaign workflow:", chatId);

          // Get the actual data from payload
          const payloadData = lastCompletedPayload?.result?.data;
          agentIdName = payloadData?.agent_id;

          const lastAgentId = payloadData?.agent_id;
          const question = payloadData?.question;
          const agentResponse = payloadData?.response;

          let finalResponse: any = {
            finalAnswer: "",
            brandReviewerAnswer: "",
            twitterAnswer: "",
            linkedinAnswer: "",
            instagramAnswer: "",
            images: "",
            socialMediaImages: "",
            socialMediaLocalizer: "",
            question: question,
          };

          if (
            lastAgentId === "utility_agent" ||
            lastAgentId === "utility_agent_redline_intake" ||
            lastAgentId === "utility_agent_redline_doc_analyzer" ||
            lastAgentId === "utility_agent_redline_doc_analyzer_sow_buttons" ||
            lastAgentId === "utility_agent_redline_doc_analyzer_sow_msa"
          ) {
            // if last agent id is utility_agent then just append the question result
            finalResponse = {
              ...finalResponse,
              finalAnswer: agentResponse[0]?.result,
            };
          } else {
            // else parse and send it to frontend
            if (lastAgentId === "press_release_agent") {
              // for press release agent
              const rawBrand = [...agentResponse].find(
                (item) => item.agent_id === "BrandReviewerAgent",
              )?.result;
              const rawPress = [...agentResponse].find(
                (item) => item.agent_id === "PressReleaseAgent",
              )?.result;
              const brandParsed = safeJSONParse(rawBrand);
              const pressParsed = safeJSONParse(rawPress);
              finalResponse = {
                ...finalResponse,
                finalAnswer: pressParsed,
                brandReviewerAnswer: brandParsed,
              };
            } else if (lastAgentId === "social_media_insights_agent") {
              const socialMediaImages = payloadData?.images;
              const rawTwitter = [...agentResponse].find(
                (item) => item.agent_id === "TwitterAgent",
              )?.result;
              const rawLinkedin = [...agentResponse].find(
                (item) => item.agent_id === "LinkedinAgent",
              )?.result;
              const rawInstagram = [...agentResponse].find(
                (item) => item.agent_id === "InstagramAgent",
              )?.result;
              const rawInsights = [...agentResponse].find(
                (item) => item.agent_id === "SocialMediaInsightsAgent",
              )?.result;
              const rawTwitterImages = [...agentResponse].find(
                (item) => item.agent_id === "twitter_image",
              )?.result;
              const rawLinkedinImages = [...agentResponse].find(
                (item) => item.agent_id === "linkedin_image",
              )?.result;
              const rawInstagramImages = [...agentResponse].find(
                (item) => item.agent_id === "instagram_image",
              )?.result;
              const rawTwitterLoc = [...agentResponse].find(
                (item) => item.agent_id === "SocialMediaLocalizerAgent_twitter",
              )?.result;
              const rawLinkedinLoc = [...agentResponse].find(
                (item) =>
                  item.agent_id === "SocialMediaLocalizerAgent_linkedin",
              )?.result;
              const rawInstagramLoc = [...agentResponse].find(
                (item) =>
                  item.agent_id === "SocialMediaLocalizerAgent_instagram",
              )?.result;

              const images = {
                rawTwitterImages,
                rawLinkedinImages,
                rawInstagramImages,
              };
              const socialMediaLocalizer = {
                rawTwitterLoc,
                rawLinkedinLoc,
                rawInstagramLoc,
              };
              const twitterParsed = safeJSONParse(rawTwitter);
              const linkedinParsed = safeJSONParse(rawLinkedin);
              const instagramParsed = safeJSONParse(rawInstagram);
              const insightsParsed = safeJSONParse(rawInsights);
              const imagesParsed = safeJSONParse(images);
              const socialMediaImagesParsed = safeJSONParse(socialMediaImages);
              const socialMediaLocalizerParsed =
                safeJSONParse(socialMediaLocalizer);

              finalResponse = {
                ...finalResponse,
                finalAnswer: insightsParsed,
                twitterAnswer: twitterParsed,
                linkedinAnswer: linkedinParsed,
                instagramAnswer: instagramParsed,
                images: imagesParsed,
                socialMediaImages: socialMediaImagesParsed,
                socialMediaLocalizer: socialMediaLocalizerParsed,
              };
            } else if (lastAgentId === "translation_agent") {
              const lastParsed = safeJSONParse(agentResponse);
              finalResponse = {
                ...finalResponse,
                finalAnswer: lastParsed,
              };
            } else if (lastAgentId === "consolidator_agent") {
              // Handle consolidator response
              // Three scenarios:
              // 1. Initial response: agentResponse has 2 items
              //    [0] = contract_reviewer_orchestrator (individual reviews)
              //    [1] = ConsolidatorAgent (consolidated negotiation playbook)
              // 2. Update response (after accept/reject): agentResponse has 1 item
              //    [0] = ConsolidatorAgent (updated consolidated negotiation playbook)
              // 3. QA response: agentResponse contains contract_reviewer_qa_agent

              // Check if this is a QA agent response
              const hasQAAgent = agentResponse?.some(
                (item: any) => item.agent_id === "contract_reviewer_qa_agent",
              );

              if (hasQAAgent) {
                // QA response scenario: preserve the response array
                finalResponse = {
                  ...finalResponse,
                  response: agentResponse,
                };
              } else {
                // Normal consolidator response
                let consolidatedSummary;

                // Check if this is an update (single item) or initial response (two items)
                if (
                  agentResponse?.length === 1 &&
                  agentResponse[0]?.agent_id === "ConsolidatorAgent"
                ) {
                  // Update scenario: only ConsolidatorAgent in response
                  consolidatedSummary = agentResponse[0]?.result || {};
                } else if (agentResponse?.length === 2) {
                  // Initial scenario: both individual reviews and consolidated summary
                  consolidatedSummary = agentResponse[1]?.result || {};
                } else {
                  // Fallback: try to find ConsolidatorAgent in the array
                  const consolidatorItem = agentResponse?.find(
                    (item: any) => item.agent_id === "ConsolidatorAgent",
                  );
                  consolidatedSummary = consolidatorItem?.result || {};
                }

                // Show only consolidated data in the response
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: consolidatedSummary,
                };
              }
            } else if (lastAgentId === "template_reviewer_agent") {
              // for other agents- localizer, etc
              const rawLast = agentResponse[0];
              const lastParsed = safeJSONParse(rawLast?.result);
              finalResponse = {
                ...finalResponse,
                finalAnswer: lastParsed,
              };
            } else if (
              lastAgentId === "nda_orchestrator" ||
              lastAgentId === "nda_intake_agent" ||
              lastAgentId === "nda_input_review_agent" ||
              lastAgentId === "nda_counterparty_agent" ||
              lastAgentId === "nda_conflict_agent" ||
              lastAgentId === "nda_template_selector_agent" ||
              lastAgentId === "nda_draft_field_capture_agent" ||
              lastAgentId === "nda_drafting_agent" ||
              lastAgentId === "nda_validation_agent" ||
              lastAgentId === "nda_reviewer_agent" ||
              lastAgentId === "nda_parser_agent" ||
              lastAgentId === "nda_qa_agent"
            ) {
              // HITL turn: payloadData has {agent_id, question, response:[]}
              // Completed turn: payloadData has {agent_id, response:[{agent_id, result}]}
              if (
                payloadData &&
                Object.prototype.hasOwnProperty.call(payloadData, "question")
              ) {
                // HITL — use payloadData directly as finalAnswer
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: payloadData,
                };
              } else if (
                Array.isArray(agentResponse) &&
                agentResponse.length > 0
              ) {
                // Completed — get the relevant agent's result
                const ndaResult =
                  agentResponse.find(
                    (item: any) => item.agent_id === lastAgentId,
                  ) || agentResponse[agentResponse.length - 1];
                const ndaParsed = safeJSONParse(ndaResult?.result);
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: ndaParsed,
                };
              }
            } else if (
              agentResponse?.[0]?.output &&
              agentResponse?.[0]?.prompt
            ) {
              // Handle RFQ evaluator HITL responses (criteria_approval, method_selection, etc.)
              // Any agent with output (stringified JSON) and prompt (markdown) fields
              const responseObj = agentResponse[0];
              finalResponse = {
                ...finalResponse,
                finalAnswer: responseObj, // Keep the whole object with output and prompt
                // Also store IDs so component can access them
                requestId: lastCompletedPayload?.result?.requestId,
                chatId: lastCompletedPayload?.result?.chat_id,
                workflowId: lastCompletedPayload?.workflow_id,
              };
            } else if (
              lastAgentId === "memorandum-of-understanding" ||
              lastAgentId === "mou_orchestrator" ||
              lastAgentId === "mou_agent" ||
              lastAgentId === "mou_entity_agent"
            ) {
              // MoU Agent - Handle HITL and completed turns similar to NDA
              // HITL turn: payloadData has {agent_id, question, response:[]}
              // Completed turn: payloadData has {agent_id, response:[{agent_id, result}]}
              if (
                payloadData &&
                Object.prototype.hasOwnProperty.call(payloadData, "question")
              ) {
                // HITL — use payloadData directly as finalAnswer
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: payloadData,
                };
              } else if (Array.isArray(agentResponse) && agentResponse.length > 0) {
                // Completed — get the relevant agent's result
                const mouResult =
                  agentResponse.find(
                    (item: any) => item.agent_id === lastAgentId,
                  ) || agentResponse[agentResponse.length - 1];
                const mouParsed = safeJSONParse(mouResult?.result);
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: mouParsed,
                };
              } else {
                // Fallback - use payloadData directly
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: payloadData,
                };
              }
              // Store workflow IDs for HITL continuation and SSE event status
              finalResponse = {
                ...finalResponse,
                requestId: lastCompletedPayload?.result?.requestId,
                chatId: lastCompletedPayload?.result?.chat_id,
                workflowId: lastCompletedPayload?.workflow_id,
                mouWorkflowStatus: lastEventStatus, // Track SSE status for workflow completion
              };
            } else if (lastAgentId === "rfq_orchestrator") {
              // Handle RFQ orchestrator response - preserve entire response array for Q&A
              // The response array contains both rfq_qa_agent and rfq_orchestrator results
              const orchestratorResult = agentResponse?.find(
                (item: any) => item.agent_id === "rfq_orchestrator",
              );

              finalResponse = {
                ...finalResponse,
                finalAnswer: {
                  ...orchestratorResult?.result,
                  response: agentResponse, // Preserve full response array including rfq_qa_agent
                },
              };
            } else if (
              lastAgentId === "contract_reviewer_orchestrator_auto_msa" &&
              typeof agentResponse === "string"
            ) {
              // Initial MSA acknowledgment: response is plain text, not an agent array
              finalResponse = { ...finalResponse, finalAnswer: agentResponse };
            } else {
              // for other agents- localizer, etc
              if (
                agentResponse &&
                Array.isArray(agentResponse) &&
                agentResponse.length > 0
              ) {
                const rawLast = agentResponse[agentResponse.length - 1];
                const lastParsed = safeJSONParse(rawLast?.result);
                finalResponse = {
                  ...finalResponse,
                  finalAnswer: lastParsed,
                };
              }
            }
          }

          finalResponseAnswer = JSON.stringify(
            deepNormalizeStrings(finalResponse),
          );
        }

        const agentMsg: ChatMessage = {
          sessionId,
          id: uuid(),
          sender: "agent",
          message: finalResponseAnswer,
          questionId,
          timestamp: new Date().toISOString(),
          requestId: newRequestId,
          agentIdName,
          workflowId,
          chatId,
        };

        // Calculate delay to allow all progress steps to animate
        // Get current streamProgress from state
        const currentState = getState() as RootState;
        const allStreamProgress = currentState.user.streamProgress;

        // Import getStepsByAgentId to calculate total steps
        const { getAgentStartedSteps } = await import("../utils/constants");

        // Calculate total steps that will be shown
        let totalSteps = 1; // Start with 1 for initial "Thinking" step
        const processedAgents = new Set<string>();

        for (const progress of allStreamProgress) {
          if (!processedAgents.has(progress.executorId)) {
            processedAgents.add(progress.executorId);
            const agentSteps = getAgentStartedSteps(
              progress.executorId,
              isImageGeneration,
            );
            totalSteps += agentSteps.length;
          }
        }

        return { agentId, userMsg, agentMsg };
      } else {
        // NON-STREAMING LOGIC - For all other endpoints
        console.log(`Making non-streaming request to: ${agentEndpoint}`);

        // Make normal axios API call using the prepared requestBody
        const response = await axiosClient.post(
          `${agentEndpoint}`,
          requestBody,
        );

        // Log the response
        console.log("Non-streaming API Response:", response.data[0]);

        let finalResponseAnswer = "";
        let newRequestId = "";
        let agentIdName = "";
        let workflowId = "";
        let chatId: number | undefined = undefined;
        const agent_id = response.data[0]?.agent_id;
        const result = response.data[0]?.result;

        // chat_id should be at the same level as requestId
        // requestId is at result?.requestId, so chat_id should also be at result level
        chatId = result?.chat_id || response.data[0]?.chat_id;
        console.log(
          "Extracted chatId for non-streaming:",
          chatId,
          "from result:",
          result,
        );

        if (agent_id === "hr_agent_v2_hitl") {
          workflowId =
            response.data[0]?.workflow_id !== undefined
              ? (response.data[0].workflow_id ?? "")
              : workflow_id;
          // Use explicit empty requestId if sent by agent, only fall back to prevRequestId if undefined
          newRequestId =
            result?.requestId !== undefined
              ? (result.requestId ?? "")
              : prevRequestId;
          agentIdName = result?.data?.agent_name;

          const answer = result?.data?.output;
          const question = result?.data?.prompt;
          const stepNumber = result?.data?.step_num;
          finalResponseAnswer = JSON.stringify(
            deepNormalizeStrings({
              answer,
              question,
              stepNumber,
            }),
          );
        }

        // Create agent message with the response
        const agentMsg: ChatMessage = {
          sessionId,
          id: uuid(),
          sender: "agent",
          message: finalResponseAnswer,
          questionId,
          timestamp: new Date().toISOString(),
          requestId: newRequestId,
          agentIdName,
          workflowId,
          chatId,
        };

        return { agentId, userMsg, agentMsg };
      }
    } catch (err: any) {
      console.error("Error in sendMessageToAgent:", err);
      const agentMsg: ChatMessage = {
        sessionId,
        id: uuid(),
        sender: "agent",
        message: "Something went wrong",
        questionId,
        timestamp: new Date().toISOString(),
        requestId: prevRequestId,
      };
      return { agentId, userMsg, agentMsg };
    }
  },
);

// ---------------- USER PREFERENCE THUNK ----------------
export const sendUserPreferenceToAgent = createAsyncThunk(
  "user/sendUserPreferenceToAgent",
  async (
    {
      chatId,
      userPreference,
      messageId,
    }: {
      chatId: number;
      userPreference: 0 | 1; // 0 for dislike, 1 for like
      messageId: string; // To update the specific message after success
    },
    { rejectWithValue, getState },
  ) => {
    try {
      const state = getState() as RootState;
      const currentUser = state.auth.user;
      const userEmail = currentUser?.username;

      console.log("sendUserPreferenceToAgent invoked with:", {
        chatId,
        userPreference,
        messageId,
        userEmail,
      });

      if (!userEmail) {
        throw new Error("User email not found");
      }

      const requestBody = {
        chat_id: chatId,
        user_preference: userPreference,
        email_id: userEmail,
      };

      console.log("Sending request to /save_user_preferences:", requestBody);

      const response = await axiosClient.post(
        "/save_user_preferences",
        requestBody,
      );

      console.log("Response from /save_user_preferences:", response.data);

      // Return the data we need to update the message
      return {
        messageId,
        userPreference,
        apiResponse: response.data,
      };
    } catch (err: any) {
      console.error("Error in sendUserPreferenceToAgent:", err);
      return rejectWithValue(err.response?.data || "Failed to save preference");
    }
  },
);

// ---------------- SLICE ----------------
const userSlice = createSlice({
  name: "user",
  initialState,
  reducers: {
    selectAgent(state, action: PayloadAction<Agents>) {
      state.selectedAgent = action.payload;
      state.currentChat = state.chatHistory[action.payload.id] || [];

      // Reset retained IDs when switching agents for a fresh conversation
      state.workflowId = "";
      state.requestId = "";
      state.streamProgress = [];
      state.isStreaming = false;
      state.isViewingHistory = false;
    },
    resetRetainedIds(state) {
      // Manual reset if needed (e.g., starting a new conversation)
      state.workflowId = "";
      state.requestId = "";
      state.streamProgress = [];
      state.isStreaming = false;
      state.isViewingHistory = false;
      state.currentChat = []; // Clear current chat when exiting history mode
      state.rfqQuestions = {};
      state.rfqAnswers = {};
      state.rfqStepHistory = {};
      state.rfqCurrentStep = {};
    },
    saveRfqQuestion(
      state,
      action: PayloadAction<{ sessionId: string; question: RfqQuestion }>,
    ) {
      const { sessionId, question } = action.payload;
      if (!state.rfqQuestions[sessionId]) {
        state.rfqQuestions[sessionId] = {};
      }
      state.rfqQuestions[sessionId][question.question_id] = question;
      // console.log(
      //   `Redux: Saved RFQ question ${question.question_id} for session ${sessionId}`,
      // );
    },
    saveRfqAnswer(
      state,
      action: PayloadAction<{ sessionId: string; answer: RfqAnswer }>,
    ) {
      const { sessionId, answer } = action.payload;
      if (!state.rfqAnswers[sessionId]) {
        state.rfqAnswers[sessionId] = [];
      }
      // Check if answer already exists (by questionId)
      const existingIndex = state.rfqAnswers[sessionId].findIndex(
        (a) => a.questionId === answer.questionId,
      );
      if (existingIndex >= 0) {
        // Update existing answer
        state.rfqAnswers[sessionId][existingIndex] = answer;
      } else {
        // Add new answer
        state.rfqAnswers[sessionId].push(answer);
      }
      console.log(
        `Redux: Saved RFQ answer for question ${answer.questionId} in session ${sessionId}`,
      );
    },
    clearRfqData(state, action: PayloadAction<string>) {
      const sessionId = action.payload;
      delete state.rfqQuestions[sessionId];
      delete state.rfqAnswers[sessionId];
      delete state.rfqStepHistory[sessionId];
      delete state.rfqCurrentStep[sessionId];
      console.log(`Redux: Cleared RFQ data for session ${sessionId}`);
    },
    saveRfqStep(
      state,
      action: PayloadAction<{
        sessionId: string;
        stepNumber: number;
        stepName: string;
        response: any;
      }>,
    ) {
      const { sessionId, stepNumber, stepName, response } = action.payload;
      if (!state.rfqStepHistory[sessionId]) {
        state.rfqStepHistory[sessionId] = [];
      }

      // Check if this step already exists (user might be re-doing a step)
      const existingStepIndex = state.rfqStepHistory[sessionId].findIndex(
        (step) => step.stepNumber === stepNumber,
      );

      const newStep: RfqStep = {
        stepNumber,
        stepName,
        response,
        timestamp: new Date().toISOString(),
      };

      if (existingStepIndex >= 0) {
        // Update existing step
        state.rfqStepHistory[sessionId][existingStepIndex] = newStep;
      } else {
        // Add new step
        state.rfqStepHistory[sessionId].push(newStep);
      }

      // Update current step to this step
      state.rfqCurrentStep[sessionId] = stepNumber;

      console.log(
        `Redux: Saved RFQ step ${stepNumber} (${stepName}) for session ${sessionId}`,
      );
    },
    setRfqCurrentStep(
      state,
      action: PayloadAction<{ sessionId: string; stepNumber: number }>,
    ) {
      const { sessionId, stepNumber } = action.payload;
      state.rfqCurrentStep[sessionId] = stepNumber;
      console.log(
        `Redux: Set current RFQ step to ${stepNumber} for session ${sessionId}`,
      );
    },
    goBackRfqStep(state, action: PayloadAction<string>) {
      const sessionId = action.payload;
      const currentStep = state.rfqCurrentStep[sessionId];

      if (currentStep && currentStep > 1) {
        state.rfqCurrentStep[sessionId] = currentStep - 1;
        console.log(
          `Redux: Went back from step ${currentStep} to ${currentStep - 1} for session ${sessionId}`,
        );
      }
    },
    clearRfqStepsAfter(
      state,
      action: PayloadAction<{ sessionId: string; stepNumber: number }>,
    ) {
      const { sessionId, stepNumber } = action.payload;
      if (state.rfqStepHistory[sessionId]) {
        // Remove all steps after the specified step number
        state.rfqStepHistory[sessionId] = state.rfqStepHistory[
          sessionId
        ].filter((step) => step.stepNumber <= stepNumber);
        console.log(
          `Redux: Cleared RFQ steps after step ${stepNumber} for session ${sessionId}`,
        );
      }
    },
    clearRfqAnswersAfter(
      state,
      action: PayloadAction<{ sessionId: string; questionIndex: number }>,
    ) {
      const { sessionId, questionIndex } = action.payload;
      if (state.rfqAnswers[sessionId]) {
        // Keep only answers up to and including the specified index
        state.rfqAnswers[sessionId] = state.rfqAnswers[sessionId].slice(0, questionIndex + 1);
        console.log(
          `Redux: Cleared RFQ answers after index ${questionIndex} for session ${sessionId}`,
        );
      }
    },
    addStreamProgress(state, action: PayloadAction<StreamProgress>) {
      state.streamProgress.push(action.payload);
    },
    clearStreamProgress(state) {
      state.streamProgress = [];
    },
    setIsStreaming(state, action: PayloadAction<boolean>) {
      state.isStreaming = action.payload;
    },
    clearAgentChat(state, action: PayloadAction<number>) {
      const agentId = action.payload;
      if (state.chatHistory[agentId]) {
        state.chatHistory[agentId] = [];
      }
      if (state.selectedAgent.id === agentId) {
        state.currentChat = [];
        state.isViewingHistory = false;
      }
    },
    loadHistoricalChat(
      state,
      action: PayloadAction<{
        messages: Array<{
          id: number;
          role: "user" | "assistant";
          content: string;
          timestamp: string;
          responseType: string | null;
          agentIdName?: string;
          chatId?: number;
          requestId?: string;
        }>;
        workflowId: string;
      }>,
    ) {
      const { messages, workflowId } = action.payload;

      // Sort messages by timestamp in ascending order (oldest first)
      const sortedMessages = [...messages].sort((a, b) => {
        const timeA = new Date(a.timestamp).getTime();
        const timeB = new Date(b.timestamp).getTime();
        return timeA - timeB;
      });

      // Map historical messages to ChatMessage format
      // Use uuid() to ensure unique keys for each message
      const historicalMessages: ChatMessage[] = sortedMessages.map((msg) => ({
        sessionId: workflowId,
        id: uuid(), // Generate unique ID for React key
        sender: msg.role === "assistant" ? "agent" : "user",
        message: msg.content,
        questionId: msg.id.toString(),
        timestamp: msg.timestamp,
        agentIdName: msg.agentIdName || msg.responseType || undefined,
        workflowId: workflowId,
        chatId: msg.chatId,
        requestId: msg.requestId,
      }));

      // Set historical chat as current chat
      state.currentChat = historicalMessages;
      state.workflowId = workflowId;
      state.loading = false;
      state.isStreaming = false;
      state.streamProgress = [];
      state.isViewingHistory = true; // Mark as viewing history
    },
  },
  extraReducers: (builder) => {
    builder.addCase(sendMessageToAgent.pending, (state, action) => {
      state.loading = true;
      state.isStreaming = true;
      state.isViewingHistory = false; // Clear history mode when sending new message

      const {
        agentId,
        message,
        questionId,
        timestamp,
        sessionId,
        files,
        our_file,
        counterparty_file,
        isImageGeneration,
        translation,
        translationType,
        initialStreamExecutorId,
      } = action.meta.arg;

      state.streamProgress = initialStreamExecutorId
        ? [{ executorId: initialStreamExecutorId, timestamp: Date.now() }]
        : [];

      // Handle file metadata for agent 52
      let fileMetadata: Array<{ name: string; size: number }> | undefined;
      if (agentId === 52) {
        fileMetadata = [];
        if (our_file) {
          fileMetadata.push({
            name: `Our document: ${our_file.name}`,
            size: our_file.size,
          });
        }
        if (counterparty_file) {
          fileMetadata.push({
            name: `Counterparty document: ${counterparty_file.name}`,
            size: counterparty_file.size,
          });
        }
      } else {
        fileMetadata = files?.map((file) => ({
          name: file.name,
          size: file.size,
        }));
      }

      const normalizedMessage =
        agentId === 48 && typeof message === "string"
          ? (() => {
              const trimmed = message.trim();
              if (trimmed.length < 24 || trimmed.length % 2 !== 0) {
                return trimmed;
              }
              const half = trimmed.length / 2;
              return trimmed.slice(0, half) === trimmed.slice(half)
                ? trimmed.slice(0, half)
                : trimmed;
            })()
          : message;

      const userMsg: ChatMessage = {
        sessionId,
        id: questionId,
        sender: "user",
        message: normalizedMessage,
        questionId,
        timestamp,
        files: fileMetadata,
        isImageGeneration,
      };

      if (!state.chatHistory[agentId]) state.chatHistory[agentId] = [];
      state.chatHistory[agentId].push(userMsg);

      state.currentChat.push(userMsg);
    });

    builder.addCase(sendMessageToAgent.fulfilled, (state, action) => {
      state.loading = false;
      state.isStreaming = false;

      const { agentId, userMsg, agentMsg } = action.payload;

      if (!state.chatHistory[agentId]) state.chatHistory[agentId] = [];
      state.chatHistory[agentId].push(agentMsg);

      state.currentChat.push(agentMsg); // store agent message along with requestId

      // Preserve explicit empty IDs so completed workflows can clear stale state.
      if (agentMsg.workflowId !== undefined) {
        state.workflowId = agentMsg.workflowId ?? "";
      }
      if (agentMsg.requestId !== undefined) {
        state.requestId = agentMsg.requestId ?? "";
      }
    });

    builder.addCase(sendMessageToAgent.rejected, (state, action) => {
      state.loading = false;
      state.isStreaming = false;
      state.streamProgress = [];
    });

    // Listen to fetchAgents from agentSlice and auto-select agent with id=11
    builder.addCase(fetchAgents.fulfilled, (state, action) => {
      const agents = action.payload;
      const defaultAgent = agents.find((agent) => agent.id === 11); // Select marketing campaign as default

      if (defaultAgent && !state.selectedAgent.id) {
        // Only set if no agent is currently selected
        state.selectedAgent = defaultAgent;
        state.currentChat = state.chatHistory[defaultAgent.id] || [];
      }
    });

    // Handle user preference API success
    builder.addCase(sendUserPreferenceToAgent.fulfilled, (state, action) => {
      const { messageId, userPreference } = action.payload;

      // Update in currentChat
      const currentChatMsg = state.currentChat.find(
        (msg) => msg.id === messageId,
      );
      if (currentChatMsg) {
        currentChatMsg.userPreference = userPreference;
      }

      // Update in chatHistory for the selected agent
      const agentId = state.selectedAgent.id;
      if (state.chatHistory[agentId]) {
        const historyMsg = state.chatHistory[agentId].find(
          (msg) => msg.id === messageId,
        );
        if (historyMsg) {
          historyMsg.userPreference = userPreference;
        }
      }
    });
  },
});

export const {
  selectAgent,
  resetRetainedIds,
  addStreamProgress,
  clearStreamProgress,
  setIsStreaming,
  clearAgentChat,
  loadHistoricalChat,
  saveRfqQuestion,
  saveRfqAnswer,
  clearRfqData,
  saveRfqStep,
  setRfqCurrentStep,
  goBackRfqStep,
  clearRfqStepsAfter,
  clearRfqAnswersAfter,
} = userSlice.actions;
export default userSlice.reducer;
